/* components/kitchen/IngredientPickers.jsx — пикеры привязки состава.
   EntityPickerModal — async multi/single выбор сырья или п/ф (Modal; xs/sm → fullscreen).
   MultiGramsStep    — 2-й шаг: брутто (ОБЯЗАТЕЛЕН, inline-ошибка — фикс боли №52) + опц. нетто/выход.
   IngredientPicker  — combobox привязки одной строки (role=combobox + aria-activedescendant,
                       клавиатура ↑↓/Home/End/Enter/Esc; «Отвязать» — соседняя кнопка; не выходит
                       за экран; на xs/sm — нижний лист с focus-trap).
   KitchenPicker     — excludeIds исключает ТТК-предков/саму ТТК (защита от циклов п/ф).
   LinkSemiproductPicker — EntityPickerModal с фильтром kind=semiproduct.
   ХЕНДОФФ → EntityPickerModal/MultiGramsStep → common/; пикеры строки → ttk/. */

import React from 'react';
import ReactDOM from 'react-dom';
import { EntityPickerModal } from '../patterns/Overlays.jsx';
import { EmptyState, QueryBoundary } from '../patterns/States.jsx';
import { Button } from '../ui/Button.jsx';
import { Checkbox, Field, NumberInput, SearchInput } from '../ui/Field.jsx';
import { Icon } from '../ui/Icon.jsx';
import { Modal } from '../ui/Modal.jsx';
import { hooks } from '../../hooks/index.jsx';
import { pickerFetch } from '../../lib/api/index.js';
import { fmt } from '../../lib/format.js';
import { getOverlayRoot, overlay } from '../../lib/overlay.jsx';

  const { useState, useEffect, useRef, useMemo, useLayoutEffect, useId } = React;
  const { useFakeQuery, useBreakpoint } = hooks;
  const { useFocusTrap } = overlay;

  /* ── KitchenPicker — kitchen-обёртка НАД общим EntityPickerModal (S1, Overlays.jsx).
     НЕ дублируем общий пикер: переиспользуем его (фикс «один EntityPickerModal»),
     передаём kitchen-fetcher + renderItem. multi → шаг MultiGramsStep отдаёт вызывающий код. ── */
  function KitchenPicker({ open, kind = 'card', multi = true, title, onClose, onConfirm, excludeIds, fetcher: fetcherProp }) {
    const Picker = EntityPickerModal; // общий каркас
    // excludeIds — id ТТК, недоступных для привязки п/ф (сама ТТК + её предки): защита от циклов
    // (канон: п/ф МОЖЕТ содержать п/ф, активный инвариант — запрет циклов, MAX_RECIPE_DEPTH=10).
    const exclude = useMemo(() => new Set((excludeIds || []).map((x) => String(x))), [excludeIds]);
    // Поиск: инъектированный fetcher (если передан вызывающим) ИЛИ боевой pickerFetch
    // по умолчанию. Раньше тут жёстко стоял fakeApi.listPickerEntities и проп fetcher
    // молча игнорировался — состав ТТК подтягивался из мока даже на реальном бэке.
    const base = fetcherProp || ((q) => pickerFetch(kind, q));
    const fetcher = async (q) => {
      const list = await base(q);
      return exclude.size ? (list || []).filter((it) => !exclude.has(String(it.id))) : list;
    };
    const renderItem = (it) => (
      <>
        <span className="picker-item__label truncate">{it.kind === 'ttk' ? '▦ ' : ''}{it.label}</span>
        <span className="picker-item__hint t-caption truncate">{[it.group, it.hint].filter(Boolean).join(' · ')}</span>
      </>
    );
    const defTitle = kind === 'semiproduct' ? 'Добавить полуфабрикат' : 'Добавить ингредиенты';
    return (
      <Picker open={open} onClose={onClose} multi={multi} size="md"
        title={title || defTitle}
        placeholder={kind === 'semiproduct' ? 'Поиск по полуфабрикатам…' : 'Поиск по сырью…'}
        fetcher={fetcher} renderItem={renderItem}
        submitLabel="Добавить"
        onSubmit={(picked) => onConfirm && onConfirm(picked)} />
    );
  }

  /* ───────────────────────── MultiGramsStep ───────────────────────── */
  function MultiGramsStep({ open, entities = [], onBack, onConfirm, onClose }) {
    const [vals, setVals] = useState({});
    const [touched, setTouched] = useState(false);
    useEffect(() => {
      if (open) {
        const init = {}; entities.forEach((e) => { init[e.id] = { brutto: '', netto: '', weight: '' }; });
        setVals(init); setTouched(false);
      }
    }, [open, entities]);

    const set = (id, key, v) => setVals((s) => ({ ...s, [id]: { ...s[id], [key]: v } }));
    const invalid = (id) => { const v = vals[id]; return !v || v.brutto === '' || v.brutto == null || Number(v.brutto) <= 0; };
    const anyInvalid = entities.some((e) => invalid(e.id));

    const confirm = () => {
      setTouched(true);
      if (anyInvalid) return;
      const rows = entities.map((e) => {
        const v = vals[e.id];
        return {
          name_raw: e.label,
          target_kind: e.kind, target_id: e.id, target_label: e.label,
          amount_brutto_g: Number(v.brutto),
          amount_netto_g: v.netto === '' ? Number(v.brutto) : Number(v.netto),
          weight_in_dish_g: v.weight === '' ? (v.netto === '' ? Number(v.brutto) : Number(v.netto)) : Number(v.weight),
          unit: 'г',
        };
      });
      onConfirm && onConfirm(rows);
    };

    return (
      <Modal open={open} onClose={onClose} title="Укажите массу (брутто)" size="sm"
        footerLeft={<Button variant="ghost" iconLeft="arrow-left" onClick={onBack}>Назад</Button>}
        footer={<Button variant="primary" onClick={confirm}>Добавить в состав</Button>}>
        <div className="grams-step">
          <p className="t-body-sm text-dim grams-step__hint">Брутто — масса сырья до обработки. Поле обязательно, иначе себестоимость не посчитается.</p>
          {entities.map((e) => (
            <div className="grams-step__item" key={e.id}>
              <div className="grams-step__name truncate">{e.label}</div>
              <div className="grams-step__fields">
                <Field label="Брутто, г" error={touched && invalid(e.id) ? 'Укажите брутто' : null} required>
                  <NumberInput value={vals[e.id] ? vals[e.id].brutto : ''} unit="г" step={5} min={0}
                    invalid={touched && invalid(e.id)} onChange={(v) => set(e.id, 'brutto', v)} />
                </Field>
                <Field label="Нетто, г">
                  <NumberInput value={vals[e.id] ? vals[e.id].netto : ''} unit="г" step={5} min={0} onChange={(v) => set(e.id, 'netto', v)} />
                </Field>
                <Field label="Выход, г">
                  <NumberInput value={vals[e.id] ? vals[e.id].weight : ''} unit="г" step={5} min={0} onChange={(v) => set(e.id, 'weight', v)} />
                </Field>
              </div>
            </div>
          ))}
        </div>
      </Modal>
    );
  }

  /* ── MobilePickerSheet — нижний лист пикера строки на xs/sm.
     useFocusTrap: ловушка Tab + возврат фокуса на триггер + Esc (закрытие даже когда
     фокус вне инпута — фикс P2-12). role=dialog/aria-modal как у Modal. ── */
  function MobilePickerSheet({ onClose, children }) {
    const sheetRef = useRef(null);
    useFocusTrap(sheetRef, { active: true, onEscape: onClose });
    return (
      <div className="overlay overlay--sheet" style={{ zIndex: 'var(--z-popover)' }}>
        <div className="overlay__backdrop" onClick={onClose} />
        <div className="select-sheet" ref={sheetRef} role="dialog" aria-modal="true" aria-label="Привязать строку">
          <div className="select-sheet__head">
            <span className="t-h4 grow">Привязать строку</span>
            <button type="button" className="modal__close" onClick={onClose} aria-label="Закрыть"><Icon name="close" size={18} /></button>
          </div>
          {children}
        </div>
      </div>
    );
  }

  /* ───────────────────────── IngredientPicker (combobox) ─────────────────────────
     Привязка строки к карточке сырья: клавиатура ↑↓/Enter/Esc, не выходит за экран,
     fullscreen на xs/sm. Расширен (S7, аддитивно):
       • candidates — блок «Предложено AI» [{card_id,name,score,reason}] вверху списка
         (участвует в клавиатурной навигации; фикс боли №36 — combobox с AI-подсказками);
       • fetcher(q) — переопределяемый поиск (инвентаризация инъектирует pickerFetch('card')).
     Без новых пропсов поведение прежнее (Кухня/ТТК), поиск — боевой pickerFetch. */
  function IngredientPicker({ value, onChange, kind = 'card', placeholder = 'Не привязан', className, candidates, fetcher }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');
    const [debounced, setDebounced] = useState('');
    const [active, setActive] = useState(0);
    const [pos, setPos] = useState(null);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    const inputRef = useRef(null);
    const timer = useRef(null);
    const { isMobile } = useBreakpoint();
    // стабильные id для combobox-семантики: listbox + опции (aria-controls / aria-activedescendant)
    const listId = useId();
    const optId = (i) => listId + '-opt-' + i;

    useEffect(() => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setDebounced(q), 250);
      return () => timer.current && clearTimeout(timer.current);
    }, [q]);

    const doFetch = fetcher || ((s) => pickerFetch(kind, s));
    const query = useFakeQuery('row-picker-' + kind, () => (open ? doFetch(debounced) : Promise.resolve([])), [open, debounced, kind]);
    const results = query.data || [];
    // Блок «Предложено AI» показываем только без поискового запроса
    const showCands = !!(candidates && candidates.length) && !debounced.trim();
    const candOpts = showCands
      ? candidates.map((c) => ({ id: c.card_id, kind: 'card', label: c.name, _cand: { score: c.score, reason: c.reason } }))
      : [];
    // единый навигируемый список: AI-кандидаты + результаты поиска
    const items = [...candOpts, ...results];

    const place = () => {
      const t = triggerRef.current; if (!t) return;
      const r = t.getBoundingClientRect();
      const w = Math.max(r.width, 240);
      const ph = 300;
      let top = r.bottom + 4;
      if (top + ph > window.innerHeight - 8) top = Math.max(8, r.top - ph - 4);
      let left = Math.min(r.left, window.innerWidth - w - 8);
      left = Math.max(8, left);
      setPos({ top, left, width: w });
    };
    useLayoutEffect(() => { if (open && !isMobile) place(); /* eslint-disable-next-line */ }, [open, items.length]);
    useEffect(() => {
      if (!open) { setQ(''); setActive(0); return; }
      setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
      const onDoc = (e) => {
        if (panelRef.current && panelRef.current.contains(e.target)) return;
        if (triggerRef.current && triggerRef.current.contains(e.target)) return;
        setOpen(false);
      };
      const reflow = () => !isMobile && place();
      document.addEventListener('mousedown', onDoc);
      window.addEventListener('resize', reflow, true);
      window.addEventListener('scroll', reflow, true);
      return () => {
        document.removeEventListener('mousedown', onDoc);
        window.removeEventListener('resize', reflow, true);
        window.removeEventListener('scroll', reflow, true);
      };
    }, [open, isMobile]);

    const pick = (e) => { onChange && onChange(e); setOpen(false); };
    const clear = (ev) => { if (ev) ev.stopPropagation(); onChange && onChange(null); };
    const onKey = (e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
      else if (e.key === 'Home') { e.preventDefault(); setActive(0); }
      else if (e.key === 'End') { e.preventDefault(); setActive(Math.max(0, items.length - 1)); }
      else if (e.key === 'Enter') { e.preventDefault(); if (items[active]) pick(items[active]); }
      else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
    };

    const panel = (
      <div className="row-picker__panel" ref={panelRef}
        style={isMobile ? undefined : { position: 'fixed', top: pos ? pos.top : 0, left: pos ? pos.left : 0, width: pos ? pos.width : 240, zIndex: 'var(--z-popover)' }}>
        <div className="row-picker__search">
          <Icon name="search" size={15} />
          <input ref={inputRef} className="row-picker__input" value={q} onChange={(e) => { setQ(e.target.value); setActive(0); }}
            onKeyDown={onKey} placeholder={kind === 'semiproduct' ? 'Поиск п/ф…' : 'Поиск сырья…'}
            role="combobox" aria-expanded={open} aria-controls={listId} aria-autocomplete="list"
            aria-activedescendant={items.length && items[active] ? optId(active) : undefined} />
        </div>
        <div className="row-picker__list" role="listbox" id={listId}>
          {showCands && (
            <div className="row-picker__group">
              <div className="row-picker__group-head t-eyebrow"><Icon name="sparkles" size={12} /> Предложено AI</div>
              {candOpts.map((e, i) => (
                <button type="button" key={'c' + e.id} role="option" id={optId(i)} aria-selected={i === active}
                  className={'row-picker__opt row-picker__opt--cand' + (i === active ? ' is-active' : '')}
                  onMouseEnter={() => setActive(i)} onClick={() => pick(e)}>
                  <span className="grow truncate">{e.label}</span>
                  <span className={'row-picker__score num' + (e._cand.score >= 85 ? ' is-ok' : e._cand.score >= 60 ? ' is-warn' : ' is-bad')}>{e._cand.score}%</span>
                  {e._cand.reason && <span className="row-picker__reason t-caption">{e._cand.reason}</span>}
                </button>
              ))}
            </div>
          )}
          {showCands && <div className="row-picker__group-head t-eyebrow">Поиск по сырью</div>}
          {query.loading
            ? <div className="row-picker__loading"><Icon name="spinner" size={16} /> Поиск…</div>
            : results.length === 0
              ? <div className="row-picker__empty t-body-sm text-mute">{debounced.trim() ? 'Ничего не найдено' : 'Начните вводить название'}</div>
              : results.slice(0, 40).map((e, ri) => {
                const i = candOpts.length + ri;
                return (
                  <button type="button" key={e.id} role="option" id={optId(i)} aria-selected={i === active}
                    className={'row-picker__opt' + (i === active ? ' is-active' : '')}
                    onMouseEnter={() => setActive(i)} onClick={() => pick(e)}>
                    {e.kind === 'ttk' && <Icon name="box" size={13} className="text-mute" />}
                    <span className="grow truncate">{e.label}</span>
                    {e.hint && <span className="row-picker__hint t-caption">{e.hint}</span>}
                  </button>
                );
              })}
        </div>
      </div>
    );

    return (
      <>
        <div className={'row-picker' + (className ? ' ' + className : '')}>
          <button type="button" ref={triggerRef} className={'row-picker__trigger' + (value ? ' is-bound' : '')}
            onClick={() => setOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={open}>
            <Icon name={value ? 'link' : 'unlink'} size={14} />
            <span className="grow truncate">{value ? value.label : placeholder}</span>
            {!value && <Icon name="chevron-down" size={14} className="text-mute" />}
          </button>
          {/* «Отвязать» — соседняя кнопка, не вложена в триггер (фикс «кнопка в кнопке»; достижима с клавиатуры) */}
          {value && (
            <button type="button" className="row-picker__clear" onClick={clear} aria-label="Отвязать">
              <Icon name="close" size={13} />
            </button>
          )}
        </div>
        {open && !isMobile && ReactDOM.createPortal(panel, getOverlayRoot())}
        {open && isMobile && ReactDOM.createPortal(
          <MobilePickerSheet onClose={() => setOpen(false)}>{panel}</MobilePickerSheet>,
          getOverlayRoot())}
      </>
    );
  }

  /* ── LinkSemiproductPicker — пикер п/ф (обёртка над общим EntityPickerModal) ── */
  function LinkSemiproductPicker(props) {
    return <KitchenPicker {...props} kind="semiproduct" multi={props.multi != null ? props.multi : false} title={props.title || 'Привязать полуфабрикат'} />;
  }

export { KitchenPicker };
export { MultiGramsStep };
export { IngredientPicker };
export { LinkSemiproductPicker };
